import { RootState } from "../../../../Redux/store";
import styles from "./MyProfile.module.scss";
import { useSelector } from "react-redux";
import moment from "moment";
import React from "react";

type ProfileMember = {
  memberId?: number;
  firstName?: string;
  lastName?: string;
  nationalId?: string;
  email?: string;
  phonenumber?: string | null;
  registeredAt?: string | null;
  isActive?: boolean;
  issubscriptionfailed?: boolean | null;
};

type ProfileBuilding = {
  buildingId?: number;
  name?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
};

type ProfileLease = {
  leaseId?: number;
  unit?: string;
  startDate?: string;
  endDate?: string;
  onHold?: boolean;
  isActive?: boolean;
};

type ProfileAccessKey = {
  keyId?: number;
  key?: string;
  name?: string;
};

type ProfileLastContract = {
  contractId?: number;
  acceptedAt?: string;
  packageId?: number;
  documentPath?: string;
};

type ProfilePackage = {
  packageId?: number;
  packageName?: string;
  packageSubExpire?: string;
  packageSubStartDate?: string;
  packagePrice?: number;
  isFamily?: boolean;
  noofMember?: number;
  period?: string;
};

type ParkingItem = {
  memberId?: number;
  memberParkingId?: number;
  parkingId?: number;
  amenityId?: number;
  amenityName?: string;
  noofvehicle?: number;
  isActive?: boolean;
};

type AddOnItem = {
  memberId?: number;
  memberAddOnId?: number;
  addonId?: number;
  amenityId?: number;
  amenityName?: string;
  noofAddonunits?: number;
  isActive?: boolean;
};

type ProfileByIdData = {
  member?: ProfileMember;
  building?: ProfileBuilding;
  lease?: ProfileLease;
  accessKey?: ProfileAccessKey;
  lastContract?: ProfileLastContract;
  packages?: ProfilePackage | null;
  parking?: ParkingItem[];
  addOns?: AddOnItem[];
};

const toTypedArray = <T,>(value: unknown): T[] =>
  Array.isArray(value) ? (value as T[]) : [];

function MyProfile() {
  const { profileByIdData } = useSelector((state: RootState) => state.profile);
  const profile = profileByIdData as ProfileByIdData | null;

  const member = profile?.member;
  const building = profile?.building;
  const lease = profile?.lease;
  const accessKey = profile?.accessKey;
  const lastContract = profile?.lastContract;
  const packages = profile?.packages;
  const parking = toTypedArray<ParkingItem>(profile?.parking);
  const addOns = toTypedArray<AddOnItem>(profile?.addOns);

  const formatDate = (date: string | null | undefined) =>
    date ? moment(date).format("MM/DD/YYYY") : "—";

  const formatDateTime = (date: string | null | undefined) =>
    date ? moment(date).format("MM/DD/YYYY hh:mm A") : "—";

  const displayValue = (value: string | number | null | undefined) =>
    value !== null && value !== undefined && value !== "" ? String(value) : "—";

  return (
    <div className={styles.mainContainer}>
      <div className={styles.mainContainerBox}>
        <div className={styles.headingContainer}>
          <p className={styles.heading}>Profile Information</p>
        </div>
        <div className={styles.fieldContainer}>
          <p className={styles.label}>First Name</p>
          <p className={styles.value}>{displayValue(member?.firstName)}</p>
        </div>
        <div className={styles.fieldContainer}>
          <p className={styles.label}>Last Name</p>
          <p className={styles.value}>{displayValue(member?.lastName)}</p>
        </div>
        <div className={styles.fieldContainer}>
          <p className={styles.label}>National ID</p>
          <p className={styles.value}>{displayValue(member?.nationalId)}</p>
        </div>
        <div className={styles.fieldContainer}>
          <p className={styles.label}>Email</p>
          <p className={styles.value}>{displayValue(member?.email)}</p>
        </div>
        <div className={styles.fieldContainer}>
          <p className={styles.label}>Phone Number</p>
          <p className={styles.value}>{displayValue(member?.phonenumber)}</p>
        </div>
        <div className={styles.fieldContainer}>
          <p className={styles.label}>Registered At</p>
          <p className={styles.value}>{formatDateTime(member?.registeredAt)}</p>
        </div>
        <div className={styles.checkboxField}>
          <p className={styles.label}>Is Active</p>
          <input
            type="checkbox"
            checked={member?.isActive === true}
            disabled
            readOnly
          />
        </div>
        <div className={styles.checkboxField}>
          <p className={styles.label}>Subscription Failed</p>
          <input
            type="checkbox"
            checked={member?.issubscriptionfailed === true}
            disabled
            readOnly
          />
        </div>
      </div>

      {building && (
        <div className={styles.mainContainerBox}>
          <div className={styles.headingContainer}>
            <p className={styles.heading}>Building</p>
          </div>
          <div className={styles.fieldContainer}>
            <p className={styles.label}>Name</p>
            <p className={styles.value}>{displayValue(building?.name)}</p>
          </div>
          <div className={styles.fieldContainer}>
            <p className={styles.label}>Address</p>
            <p className={styles.value}>{displayValue(building?.address)}</p>
          </div>
          <div className={styles.fieldContainer}>
            <p className={styles.label}>City</p>
            <p className={styles.value}>{displayValue(building?.city)}</p>
          </div>
          <div className={styles.fieldContainer}>
            <p className={styles.label}>State</p>
            <p className={styles.value}>{displayValue(building?.state)}</p>
          </div>
          <div className={styles.fieldContainer}>
            <p className={styles.label}>Zip</p>
            <p className={styles.value}>{displayValue(building?.zip)}</p>
          </div>
        </div>
      )}

      {lease && (
        <div className={styles.mainContainerBox}>
          <div className={styles.headingContainer}>
            <p className={styles.heading}>Lease</p>
          </div>
          <div className={styles.fieldContainer}>
            <p className={styles.label}>Unit</p>
            <p className={styles.value}>{displayValue(lease?.unit)}</p>
          </div>
          <div className={styles.fieldContainer}>
            <p className={styles.label}>Start Date</p>
            <p className={styles.value}>{formatDate(lease?.startDate)}</p>
          </div>
          <div className={styles.fieldContainer}>
            <p className={styles.label}>End Date</p>
            <p className={styles.value}>{formatDate(lease?.endDate)}</p>
          </div>
          <div className={styles.checkboxField}>
            <p className={styles.label}>On Hold</p>
            <input
              type="checkbox"
              checked={lease?.onHold === true}
              disabled
              readOnly
            />
          </div>
          <div className={styles.checkboxField}>
            <p className={styles.label}>Is Active</p>
            <input
              type="checkbox"
              checked={lease?.isActive === true}
              disabled
              readOnly
            />
          </div>
        </div>
      )}

      {accessKey && (
        <div className={styles.mainContainerBox}>
          <div className={styles.headingContainer}>
            <p className={styles.heading}>Access Key</p>
          </div>
          <div className={styles.fieldContainer}>
            <p className={styles.label}>Name</p>
            <p className={styles.value}>{displayValue(accessKey?.name)}</p>
          </div>
          <div className={styles.fieldContainer}>
            <p className={styles.label}>Key</p>
            <p className={styles.value}>{displayValue(accessKey?.key)}</p>
          </div>
        </div>
      )}

      {lastContract && (
        <div className={styles.mainContainerBox}>
          <div className={styles.headingContainer}>
            <p className={styles.heading}>Last Contract</p>
          </div>
          <div className={styles.fieldContainer}>
            <p className={styles.label}>Contract ID</p>
            <p className={styles.value}>
              {displayValue(lastContract?.contractId)}
            </p>
          </div>
          <div className={styles.fieldContainer}>
            <p className={styles.label}>Accepted At</p>
            <p className={styles.value}>
              {formatDateTime(lastContract?.acceptedAt)}
            </p>
          </div>
          <div className={styles.fieldContainer}>
            <p className={styles.label}>Package ID</p>
            <p className={styles.value}>
              {displayValue(lastContract?.packageId)}
            </p>
          </div>
          {lastContract?.documentPath && (
            <div className={styles.fieldContainer}>
              <p className={styles.label}>Document</p>
              <p className={styles.value}>{lastContract?.documentPath}</p>
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => {
                  if (lastContract?.documentPath) {
                    window.open(lastContract.documentPath, "_blank");
                  }
                }}
              >
                Open Link
              </button>
            </div>
          )}
        </div>
      )}

      {packages && (
        <div className={styles.mainContainerBox}>
          <div className={styles.headingContainer}>
            <p className={styles.heading}>Package</p>
          </div>
          <div className={styles.fieldContainer}>
            <p className={styles.label}>Package Name</p>
            <p className={styles.value}>
              {displayValue(packages?.packageName)} -{" "}
              {packages?.period
                ? packages?.period?.toLowerCase().charAt(0).toUpperCase() +
                  packages?.period?.toLowerCase().slice(1)
                : "N/A"}
            </p>
          </div>
          <div className={styles.fieldContainer}>
            <p className={styles.label}>Package ID</p>
            <p className={styles.value}>{displayValue(packages?.packageId)}</p>
          </div>
          <div className={styles.fieldContainer}>
            <p className={styles.label}>Price</p>
            <p className={styles.value}>
              {packages?.packagePrice != null
                ? `$${packages.packagePrice}`
                : "—"}
            </p>
          </div>
          <div className={styles.fieldContainer}>
            <p className={styles.label}>Subscription Start</p>
            <p className={styles.value}>
              {formatDateTime(packages?.packageSubStartDate)}
            </p>
          </div>
          <div className={styles.fieldContainer}>
            <p className={styles.label}>Subscription Expires</p>
            <p className={styles.value}>
              {formatDateTime(packages?.packageSubExpire)}
            </p>
          </div>
          <div className={styles.fieldContainer}>
            <p className={styles.label}>Number of Members</p>
            <p className={styles.value}>{displayValue(packages?.noofMember)}</p>
          </div>
          <div className={styles.checkboxField}>
            <p className={styles.label}>Family Package</p>
            <input
              type="checkbox"
              checked={packages?.isFamily === true}
              disabled
              readOnly
            />
          </div>
        </div>
      )}

      <div className={styles.mainContainerBox}>
        <div className={styles.headingContainer}>
          <p className={styles.heading}>Parking</p>
        </div>
        {parking?.length > 0 ? (
          <div className={styles.listSection}>
            {parking.map((item: ParkingItem, index: number) => (
              <div
                key={item?.memberParkingId ?? index}
                className={styles.listItem}
              >
                <div className={styles.fieldContainer}>
                  <p className={styles.label}>Amenity</p>
                  <p className={styles.value}>
                    {displayValue(item?.amenityName)}
                  </p>
                </div>
                <div className={styles.fieldContainer}>
                  <p className={styles.label}>Number of Vehicles</p>
                  <p className={styles.value}>
                    {displayValue(item?.noofvehicle)}
                  </p>
                </div>
                <div className={styles.checkboxField}>
                  <p className={styles.label}>Is Active</p>
                  <input
                    type="checkbox"
                    checked={item?.isActive === true}
                    disabled
                    readOnly
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className={styles.emptyMessage}>No parking assigned.</p>
        )}
      </div>

      <div className={styles.mainContainerBox}>
        <div className={styles.headingContainer}>
          <p className={styles.heading}>Add-ons</p>
        </div>
        {addOns?.length > 0 ? (
          <div className={styles.listSection}>
            {addOns.map((item: AddOnItem, index: number) => (
              <div
                key={item?.memberAddOnId ?? index}
                className={styles.listItem}
              >
                <div className={styles.fieldContainer}>
                  <p className={styles.label}>Amenity</p>
                  <p className={styles.value}>
                    {displayValue(item?.amenityName)}
                  </p>
                </div>
                <div className={styles.fieldContainer}>
                  <p className={styles.label}>Number of Units</p>
                  <p className={styles.value}>
                    {displayValue(item?.noofAddonunits)}
                  </p>
                </div>
                <div className={styles.checkboxField}>
                  <p className={styles.label}>Is Active</p>
                  <input
                    type="checkbox"
                    checked={item?.isActive === true}
                    disabled
                    readOnly
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className={styles.emptyMessage}>No add-ons assigned.</p>
        )}
      </div>
    </div>
  );
}

export default MyProfile;
